import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Brain, TrendingUp, TrendingDown, Minus, Loader2, History, Target, Activity, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const COL = { BULL: "#22c55e", BASE: "#a1a1aa", BEAR: "#f43f5e" };
const ICON = { BULL: TrendingUp, BASE: Minus, BEAR: TrendingDown };

const Bar = ({ pct, color }) => (
  <div className="h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}66` }} />
  </div>
);

const ScenarioCard = ({ s }) => {
  const Ic = ICON[s.type];
  const c = COL[s.type];
  return (
    <div data-testid={`scenario-${s.type}`} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 font-mono text-xs font-bold" style={{ color: c }}>
          <Ic size={13} /> {s.type}
        </div>
        <div className="font-mono text-lg font-bold" style={{ color: c }}>{s.prob}%</div>
      </div>
      <Bar pct={s.prob} color={c} />
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px] text-zinc-400">
        <div>Target <span className="text-zinc-200">{s.target}</span></div>
        <div>Range <span className="text-zinc-200">{s.range?.[0]}–{s.range?.[1]}</span></div>
        {s.invalidation != null && <div className="col-span-2">Invalidation <span className="text-rose-400">{s.invalidation}</span></div>}
      </div>
    </div>
  );
};

export const ForecastModal = ({ open, onOpenChange, symbol }) => {
  const [tab, setTab] = useState("scenarios");
  const [fc, setFc] = useState(null);
  const [acc, setAcc] = useState(null);
  const [tm, setTm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !symbol) return;
    setLoading(true); setTm(null);
    Promise.all([api.forecast(symbol).catch(() => null), api.forecastAccuracy().catch(() => null)])
      .then(([f, a]) => { setFc(f?.forecast || null); setAcc(a || null); })
      .finally(() => setLoading(false));
  }, [open, symbol]);

  const runTM = async () => {
    setTm("loading");
    try { setTm(await api.timeMachine(symbol, 24)); } catch { setTm({ ok: false, error: "failed" }); }
  };

  const tabs = [["scenarios", "Scenarios", Brain], ["timemachine", "Time Machine", History], ["accuracy", "Accuracy", Target]];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#0b0d10] border-zinc-800 text-white" data-testid="forecast-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <Brain size={16} className="text-cyan-400" /> Scenario Engine · <span className="text-cyan-300">{symbol}</span>
            {fc?.regime && <span className="rounded-sm bg-cyan-500/15 px-2 py-0.5 font-mono text-[10px] text-cyan-300">{fc.regime}</span>}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500">
            Measurable probabilistic scenarios — not guaranteed predictions. Every forecast is stored and scored.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3 flex gap-1 rounded-md bg-zinc-900 p-0.5">
          {tabs.map(([k, label, Ic]) => (
            <button key={k} data-testid={`forecast-tab-${k}`} onClick={() => setTab(k)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 font-mono text-[11px] transition-colors ${tab === k ? "bg-zinc-800 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}>
              <Ic size={12} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 font-mono text-xs text-zinc-500"><Loader2 size={14} className="animate-spin" /> Modeling…</div>
        ) : tab === "scenarios" ? (
          !fc ? <div className="py-12 text-center font-mono text-xs text-zinc-500">Not enough data.</div> : (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 font-mono text-[10px] text-zinc-400">
                <span>Confidence <span className="text-zinc-200">{Math.round(fc.confidence * 100)}%</span></span>
                <span>Horizon <span className="text-zinc-200">{fc.horizon_min}m</span></span>
                <span>Exp. move <span className="text-zinc-200">±{fc.expected_move}</span></span>
                <span>Bias <span style={{ color: fc.bias >= 0 ? COL.BULL : COL.BEAR }}>{fc.bias >= 0 ? "+" : ""}{fc.bias}</span></span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {fc.scenarios.map((s) => <ScenarioCard key={s.type} s={s} />)}
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
                <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Why — primary: {fc.primary}</div>
                {fc.why.supporting.map((w, i) => (
                  <div key={`s${i}`} className="flex items-center gap-1.5 font-mono text-[11px] text-emerald-400"><ChevronRight size={11} /> {w.factor} <span className="text-zinc-600">({w.weight > 0 ? "+" : ""}{w.weight})</span></div>
                ))}
                {fc.why.opposing.map((w, i) => (
                  <div key={`o${i}`} className="flex items-center gap-1.5 font-mono text-[11px] text-rose-400"><Minus size={11} /> {w.factor} <span className="text-zinc-600">({w.weight})</span></div>
                ))}
              </div>
            </div>
          )
        ) : tab === "timemachine" ? (
          <div className="space-y-3">
            <p className="font-mono text-[11px] text-zinc-500">Replay from 24 bars ago using only data available then (no look-ahead), then reveal what actually happened.</p>
            <button data-testid="run-timemachine" onClick={runTM} className="rounded-sm bg-cyan-500/15 border border-cyan-600/40 px-4 py-2 font-mono text-xs text-cyan-300 hover:bg-cyan-500/25">
              <History size={12} className="mr-1.5 inline" /> Run Time Machine
            </button>
            {tm === "loading" && <div className="flex items-center gap-2 py-6 font-mono text-xs text-zinc-500"><Loader2 size={14} className="animate-spin" /> Replaying…</div>}
            {tm && tm !== "loading" && (tm.ok ? (
              <div className="space-y-2">
                <div className="grid grid-cols-1 gap-2">{tm.forecast.scenarios.map((s) => <ScenarioCard key={s.type} s={s} />)}</div>
                {tm.result && (
                  <div data-testid="timemachine-result" className={`rounded-md border p-3 font-mono text-[11px] ${tm.result.directional_correct ? "border-emerald-700/50 bg-emerald-500/10 text-emerald-300" : "border-rose-700/50 bg-rose-500/10 text-rose-300"}`}>
                    <div className="flex items-center gap-1.5 font-bold"><Activity size={12} /> Reality: price moved {tm.result.actual_move} → {tm.result.actual_dir}</div>
                    <div className="mt-1 text-zinc-400">Predicted primary <span className="text-zinc-200">{tm.result.primary}</span> · Directional {tm.result.directional_correct ? "✓ CORRECT" : "✗ wrong"} · Range hit {tm.result.range_hit || "none"} · Brier {tm.result.brier}</div>
                  </div>
                )}
              </div>
            ) : <div className="py-6 text-center font-mono text-xs text-rose-400">{tm.error}</div>)}
          </div>
        ) : (
          <div className="space-y-3" data-testid="forecast-accuracy">
            {!acc ? <div className="py-8 text-center font-mono text-xs text-zinc-500">No data.</div> : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 text-center">
                    <div className="font-mono text-2xl font-bold text-cyan-300">{acc.directional_accuracy != null ? `${acc.directional_accuracy}%` : "—"}</div>
                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">Directional</div>
                  </div>
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 text-center">
                    <div className="font-mono text-2xl font-bold text-zinc-200">{acc.avg_brier != null ? acc.avg_brier : "—"}</div>
                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">Avg Brier (↓ better)</div>
                  </div>
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 text-center">
                    <div className="font-mono text-2xl font-bold text-zinc-200">{acc.scored}<span className="text-zinc-600 text-sm">/{acc.total}</span></div>
                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">Scored · {acc.pending} pending</div>
                  </div>
                </div>
                <div className="max-h-[240px] overflow-y-auto rounded-md border border-zinc-800">
                  {acc.recent?.length ? acc.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-zinc-900 px-3 py-1.5 font-mono text-[11px] last:border-0">
                      <span className="text-zinc-300">{r.symbol} · {r.primary}</span>
                      <span className={r.result?.directional_correct ? "text-emerald-400" : "text-rose-400"}>
                        {r.result?.directional_correct ? "✓" : "✗"} {r.result?.actual_dir} · brier {r.result?.brier}
                      </span>
                    </div>
                  )) : <div className="py-8 text-center font-mono text-xs text-zinc-500">No scored forecasts yet — they score once each horizon elapses.</div>}
                </div>
                <p className="font-mono text-[10px] text-zinc-600">Forecasts are stored on creation and scored against realized price at their horizon. Accuracy is measured, not asserted.</p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
